<template>
    <div>
        <BlockContainer
            :id="'sample-pack-selector-' + slugify(data.heading)"
            :background-color="data.backgroundColor"
            :bottom-angle="data.bottomAngle"
            :angle-background-color="
                data.nextBackgroundColor ? data.nextBackgroundColor : null
            "
            :arrow-callout="data.arrowCallout"
        >
            <template #container>
                <div class="container pt-16">
                    <h2 class="mb-8">
                        {{ data.heading }}
                    </h2>
                    <div v-if="data.note" class="flex flex-col py-2">
                        <p class="mb-6 text-sm">
                            {{ data.note }}
                        </p>
                    </div>
                    <div class="grid gap-11 md:grid-cols-3">
                        <SamplePack
                            v-for="(pack, key) in data.samplePacks"
                            :key="key"
                            :is-in-cart="isInCart(pack.product)"
                            :data="pack"
                            :index="key"
                            @add-to-cart="addToCart($event)"
                        />
                    </div>
                </div>
            </template>
        </BlockContainer>
        <AddToCartFlyout ref="addToCartFlyout" />
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import BlockContainer from '~/components/BlockContainer.vue';
import AddToCartFlyout from '~/components/cart/AddToCartFlyout.vue';
import { sendDataLayerEcommerceEvent } from '~/composables/useDataLayer';
import { createBuyable } from '~/types/Buyable';
import { CartItem } from '~/types/Cart';
import { Image } from '~/types/miscellaneous';
import { ProductVariation } from '~/types/product';
import SamplePackSelectorType from '~/types/sample-pack/SamplePackSelectorType';
import SamplePack from './components/SamplePack.vue';

defineProps<{
    data: SamplePackSelectorType;
}>();

const addToCartFlyout = ref<InstanceType<typeof AddToCartFlyout> | null>(null);
const addingToCart = ref(false);
const { loadContent, items } = useCartState();

const isInCart = (product: ProductVariation) => {
    return items.value.some((item: CartItem) => item.productId === product.id);
};

const addToCart = async (product: ProductVariation) => {
    addingToCart.value = true;

    const newProduct = createBuyable({
        id: product.id,
        image: product.image as Image,
        name: product.name,
    });

    const items: any = [];
    items.push({
        item_id: product.vEditable?.content?.database_product?.id,
        item_name: product.vEditable?.name,
        item_category:
            product.vEditable?.content?.category?.length > 0
                ? product.content?.category[0]
                : '',
        item_category2: product.vEditable?.content?.product_type,
        item_category3:
            product.vEditable?.content?.material?.length > 0
                ? product.content?.material[0]
                : '',
        item_list_name:
            product.vEditable?.content?.category?.length > 0
                ? product.content?.category[0]
                : '',
        is_sample:
            product.vEditable?.content?.database_product?.sku?.includes(
                'SAMPLE',
            ) ?? false,
        color: null,
        material:
            product.vEditable?.content?.material?.length > 0
                ? product.content?.material[0]
                : '',
        finish: null,
        size: null,
    });

    sendDataLayerEcommerceEvent('add_to_cart', '', {
        currency: 'USD',
        items: items,
    });

    await useAddToCart(
        newProduct,
        addToCartFlyout.value as unknown as boolean | undefined,
    ).finally(() => {
        addingToCart.value = false;
        loadContent({ force: true });
    });
};

onBeforeMount(() => {
    loadContent({ force: true });
});
</script>
